import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useShopContext } from '../../context/ShopContext';
import { useProductContext } from '../../context/ProductContext';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/utils/LoadingSpinner';
import Alert from '../../components/utils/Alert';
import {
  StarBorderOutlined,
  Star,
  AddShoppingCart,
  ArrowBack,
  Send,
  ChevronLeft,
  ChevronRight,
  StoreMallDirectoryRounded,
  Inventory,
  LocalShipping,
  Shield,
  FavoriteBorder,
  Favorite,
  Share,
  ZoomIn,
  ZoomOut,
  Palette,
  CheckCircle,
} from '@mui/icons-material';
import { Rating, TextField } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { Store, BranchProductColor } from '../../types';
import i18n from '../../i18n';
import { useNotification } from '../../context/NotificationContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && <div className="py-6">{children}</div>}
    </div>
  );
}

// Helper function to safely convert to number
const safeToNumber = (value: string | number | undefined | null): number => {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') return value;
  
  // Remove any non-numeric characters except decimal point
  const cleaned = String(value)
    .replace(/[^\d.-]/g, '')
    .replace(/,/g, '.');
  
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

// Format price function
const formatPrice = (price: string | number | undefined | null, currency: string = 'ج.م'): string => {
  const numPrice = safeToNumber(price);
  return `${numPrice.toFixed(2)} ${currency}`;
};

export default function ProductDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    selectedBranch,
    loading: shopLoading,
    error: shopError,
    stores,
    setSelectedStore,
  } = useShopContext();
  const {
    selectedProduct,
    loading: productLoading,
    error: productError,
    fetchProduct,
    addToCart,
    reviews,
    fetchProductReviews,
    submitReview,
    products,
  } = useProductContext();

  const [selectedColor, setSelectedColor] = useState<BranchProductColor | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewSuccess, setReviewSuccess] = useState<string | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const { user, isLoading } = useAuth();
  const { notify } = useNotification();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (id && selectedBranch?.id) {
      fetchProduct(Number(id), selectedBranch.id);
      fetchProductReviews(Number(id));
    } else if (id) {
      fetchProduct(Number(id), selectedProduct?.branch.id);
    }
  }, [id, selectedBranch]);

  useEffect(() => {
    // Set default selected color when product loads
    if (selectedProduct?.colors?.length) {
      setSelectedColor(selectedProduct.colors[0]);
      // Reset image index when color changes
      setSelectedImageIndex(0);
    }
  }, [selectedProduct]);

  // Get current images based on selected color
  const currentImages = useMemo(() => {
    if (selectedColor?.images?.length) {
      return selectedColor.images;
    }
    return selectedProduct?.product.images || [];
  }, [selectedColor, selectedProduct]);

  // Get stock for selected color or default product stock
  const currentStock = useMemo(() => {
    if (selectedColor) {
      return selectedColor.stock;
    }
    return selectedProduct?.stock || 0;
  }, [selectedColor, selectedProduct]);

  // Calculate prices safely
  const priceNumber = useMemo(() => safeToNumber(selectedProduct?.price), [selectedProduct]);
  const basePriceNumber = useMemo(() => safeToNumber(selectedProduct?.product.base_price), [selectedProduct]);

  // Calculate discount safely
  const discountAmount = useMemo(() => {
    if (basePriceNumber > priceNumber) {
      return basePriceNumber - priceNumber;
    }
    return 0;
  }, [basePriceNumber, priceNumber]);

  const discountPercentage = useMemo(() => {
    if (basePriceNumber > 0 && basePriceNumber > priceNumber) {
      return Math.round(((basePriceNumber - priceNumber) / basePriceNumber) * 100);
    }
    return 0;
  }, [basePriceNumber, priceNumber]);

  const handleAddToCart = () => {
    if (selectedProduct) {
      // Add selected color to cart if available
      const productData = {
        productId: selectedProduct.id,
        quantity,
        colorId: selectedColor?.id,
      };
      
      // Add multiple items if quantity > 1
      for (let i = 0; i < quantity; i++) {
        addToCart(selectedProduct.id, selectedColor?.id);
      }
    }
  };

  const handleSubmitReview = async () => {
    if (!rating || !comment.trim()) {
      setReviewError(t('product_detail.validation_error'));
      return;
    }
    if (!user) {
      setReviewError(t('product_detail.validation_error_register'));
      return;
    }
    try {
      await submitReview(Number(id), { rating, comment });
      setReviewSuccess(t('product_detail.review_success'));
      setRating(null);
      setComment('');
      setReviewError(null);
    } catch {
      setReviewError(t('product_detail.review_failure'));
    }
  };

  const handleColorSelect = (color: BranchProductColor) => {
    setSelectedColor(color);
    setSelectedImageIndex(0); // Reset to first image when color changes
  };

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setImageLoading(true);
  };

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) =>
      prev === 0 ? currentImages.length - 1 : prev - 1
    );
    setImageLoading(true);
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) =>
      prev === currentImages.length - 1 ? 0 : prev + 1
    );
    setImageLoading(true);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleZoomToggle = () => {
    setIsZoomed(!isZoomed);
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= currentStock) {
      setQuantity(newQuantity);
    }
  };

  const handleToggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleShareProduct = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: selectedProduct?.product.name,
          text: selectedProduct?.product.description,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Sharing cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  // Stock level indicator
  const getStockLevel = (stock: number) => {
    if (stock > 20) return 'high';
    if (stock > 5) return 'medium';
    if (stock > 0) return 'low';
    return 'out';
  };

  const getStockColor = (stock: number) => {
    if (stock > 20) return 'text-green-600';
    if (stock > 5) return 'text-yellow-600';
    if (stock > 0) return 'text-orange-600';
    return 'text-red-600';
  };

  const getStockBgColor = (stock: number) => {
    if (stock > 20) return 'bg-green-100';
    if (stock > 5) return 'bg-yellow-100';
    if (stock > 0) return 'bg-orange-100';
    return 'bg-red-100';
  };

  const getStockProgressColor = (stock: number) => {
    if (stock > 20) return 'bg-green-500';
    if (stock > 5) return 'bg-yellow-500';
    if (stock > 0) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getStockProgressWidth = (stock: number) => {
    if (stock > 20) return '100%';
    if (stock > 5) return '60%';
    if (stock > 0) return '20%';
    return '0%';
  };

  if (shopError || productError) {
    return (
      <div className="container mx-auto px-6 py-12">
        <Alert type="error" message={shopError || productError || t('common.error_occurred')} />
      </div>
    );
  }

  if (productLoading || !selectedProduct) {
    return (
      <div className="container mx-auto px-6 py-12">
        <LoadingSpinner />
      </div>
    );
  }

  const {
    branch,
    colors,
  } = selectedProduct;
  const {
    name,
    description,
    images,
    badge,
    is_featured,
    total_sold,
    average_rating,
  } = selectedProduct.product;

  const current_store: Store | undefined = stores.find(
    (st) => `${st.id}` === `${selectedProduct?.branch.store}`
  );
  const hasMultipleImages = currentImages.length > 1;
  const placeholderImage = '/assets/placeholder.png';

  // Stock level
  const stockLevel = getStockLevel(currentStock);

  return (
    <div className="container mx-auto px-4 sm:px-6 py-8">
      {/* Breadcrumb Navigation */}
      <div className="mb-6">
        <nav className="flex items-center text-sm text-gray-500">
          <button
            onClick={() => navigate('/')}
            className="hover:text-primary transition-colors"
          >
            {t('common.home')}
          </button>
          <span className="mx-2">/</span>
          <button
            onClick={() => navigate('/products')}
            className="hover:text-primary transition-colors"
          >
            {t('products.title')}
          </button>
          <span className="mx-2">/</span>
          <span className="text-gray-700 font-medium truncate">{name}</span>
        </nav>
      </div>

      {/* Main Product Layout */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left Column - Images */}
        <div className="lg:w-1/2 xl:w-2/5">
          <div className="sticky top-8">
            {/* Main Image Container */}
            <div
              className="relative bg-white rounded-2xl shadow-xl overflow-hidden group cursor-zoom-in mb-4"
              style={{ height: '400px' }}
              onClick={handleZoomToggle}
              role="button"
              tabIndex={0}
              aria-label={t('product_detail.toggle_zoom')}
              onKeyDown={(e) => e.key === 'Enter' && handleZoomToggle()}
            >
              {/* Product Badges */}
              <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
                {is_featured && (
                  <div className="inline-flex items-center px-2 py-1 rounded-md bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold shadow-lg">
                    <Star className="w-3 h-3 mr-1" />
                    {t('productCard.badges.featured')}
                  </div>
                )}
                {badge && (
                  <div className="inline-flex items-center px-2 py-1 rounded-md bg-gradient-to-r from-primary to-primary-dark text-white text-xs font-bold shadow-lg">
                    {t(`productCard.badges.${badge.toLowerCase()}`, badge)}
                  </div>
                )}
                {discountPercentage > 0 && (
                  <div className="inline-flex items-center px-2 py-1 rounded-md bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold shadow-lg">
                    -{discountPercentage}%
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
                <button
                  onClick={handleToggleFavorite}
                  className="bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
                  aria-label={
                    isFavorite
                      ? t('productCard.removeFromWishlist')
                      : t('productCard.addToWishlist')
                  }
                >
                  {isFavorite ? (
                    <Favorite className="w-5 h-5 text-red-500" />
                  ) : (
                    <FavoriteBorder className="w-5 h-5 text-gray-600" />
                  )}
                </button>
                <button
                  onClick={handleShareProduct}
                  className="bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
                  aria-label={t('product_detail.share')}
                >
                  <Share className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Main Image */}
              <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                {imageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <LoadingSpinner />
                  </div>
                )}
                <img
                  src={currentImages[selectedImageIndex]?.image || placeholderImage}
                  alt={
                    currentImages[selectedImageIndex]?.alt_text ||
                    t('product_detail.image_alt', { name, index: selectedImageIndex + 1 })
                  }
                  className={`w-full h-full object-contain transition-all duration-500 ease-in-out ${
                    isZoomed ? 'scale-150' : 'scale-100'
                  } ${!imageLoading ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={handleImageLoad}
                  style={{ display: imageLoading ? 'none' : 'block' }}
                />
              </div>

              {/* Zoom Indicator */}
              <div className="absolute bottom-4 right-4 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {isZoomed ? (
                  <>
                    <ZoomOut className="w-4 h-4 inline mr-1" />
                    {t('product_detail.zoom_out')}
                  </>
                ) : (
                  <>
                    <ZoomIn className="w-4 h-4 inline mr-1" />
                    {t('product_detail.zoom_in')}
                  </>
                )}
              </div>

              {/* Navigation Arrows */}
              {hasMultipleImages && (
                <>
                  <button
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/60 backdrop-blur-sm text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-black/80 hover:scale-110"
                    onClick={handlePrevImage}
                    aria-label={t('product_detail.previous_image')}
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/60 backdrop-blur-sm text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-black/80 hover:scale-110"
                    onClick={handleNextImage}
                    aria-label={t('product_detail.next_image')}
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails Gallery */}
            {hasMultipleImages && (
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mt-2">
                {currentImages.map((img, index) => (
                  <button
                    key={img.id}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                      selectedImageIndex === index
                        ? 'border-primary ring-2 ring-primary/20'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleImageClick(index)}
                    aria-label={t('product_detail.view_image', { index: index + 1 })}
                  >
                    <img
                      src={img.image}
                      alt={img.alt_text || t('product_detail.image_thumbnail', { name, index: index + 1 })}
                      className="w-full h-full object-cover"
                    />
                    {selectedImageIndex === index && (
                      <div className="absolute inset-0 bg-primary/10 border-2 border-primary" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Product Details */}
        <div className="lg:w-1/2 xl:w-3/5">
          <div className="space-y-6">
            {/* Product Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              {name}
            </h1>

            {/* Rating & Sold Info */}
            <div className="flex flex-wrap items-center gap-4">
              {average_rating && average_rating > 0 && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center bg-gray-50 px-3 py-1 rounded-lg">
                    <Star className="text-yellow-400 w-5 h-5 fill-yellow-400" />
                    <span className="ml-1 font-bold text-gray-900">
                      {typeof average_rating === 'number' ? average_rating.toFixed(1) : '0.0'}
                    </span>
                  </div>
                  <Rating
                    value={typeof average_rating === 'number' ? average_rating : 0}
                    readOnly
                    precision={0.5}
                    size="small"
                    icon={<Star fontSize="inherit" className="text-yellow-400" />}
                    emptyIcon={
                      <StarBorderOutlined fontSize="inherit" className="text-gray-300" />
                    }
                  />
                </div>
              )}
              {total_sold > 0 && (
                <div className="flex items-center gap-2 text-gray-600">
                  <div className="w-1 h-1 bg-gray-400 rounded-full" />
                  <span>{t('product_detail.sold_times', { count: total_sold })}</span>
                </div>
              )}
            </div>

            {/* Price Section */}
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                {discountPercentage > 0 && (
                  <div className="text-xl text-gray-400 line-through">
                    {formatPrice(basePriceNumber, t('checkout.currency'))}
                  </div>
                )}
                <div className="text-4xl font-bold text-primary">
                  {formatPrice(priceNumber, t('checkout.currency'))}
                </div>
                {discountPercentage > 0 && (
                  <div className="inline-flex items-center px-3 py-1 rounded-md bg-gradient-to-r from-red-500 to-pink-500 text-white font-bold">
                    {discountPercentage}% {t('product_detail.off')}
                  </div>
                )}
              </div>
              {discountPercentage > 0 && (
                <div className="text-sm text-green-600 font-medium">
                  {t('product_detail.save_amount', {
                    amount: discountAmount.toFixed(2),
                    percentage: discountPercentage,
                  })}
                </div>
              )}
            </div>

            {/* Colors Selection */}
            {colors && colors.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Palette className="w-5 h-5 text-gray-600" />
                    <div className="text-lg font-semibold">
                      {t('product_detail.select_color')}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {colors.length} {t('product_detail.colors_available')}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {colors.map((color) => (
                    <div
                      key={color.id}
                      className="relative group"
                      title={`${color.color_name} (${color.stock} ${t('productCard.available')})`}
                    >
                      <button
                        onClick={() => handleColorSelect(color)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all duration-300 ${
                          selectedColor?.id === color.id
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-gray-200 hover:border-gray-300'
                        } ${color.stock === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={color.stock === 0}
                      >
                        <div
                          className="w-6 h-6 rounded-full border"
                          style={{ backgroundColor: color.color_name.toLowerCase() }}
                        />
                        <span className="text-sm font-medium">{color.color_name}</span>
                        {selectedColor?.id === color.id && (
                          <CheckCircle className="w-4 h-4 text-primary" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stock Status */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Inventory className="w-5 h-5 text-gray-600" />
                  <div className="text-lg font-semibold">
                    {t('product_detail.stock_status')}
                  </div>
                </div>
                <div className={`text-sm font-semibold ${getStockColor(currentStock)}`}>
                  {stockLevel === 'high'
                    ? t('productCard.inStock')
                    : stockLevel === 'medium'
                    ? t('productCard.inStock')
                    : stockLevel === 'low'
                    ? t('productCard.lowStock')
                    : t('productCard.outOfStock')}
                </div>
              </div>
              <div className="space-y-1">
                <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${getStockProgressColor(currentStock)} transition-all duration-500`}
                    style={{ width: getStockProgressWidth(currentStock) }}
                  />
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>
                    {currentStock} {t('productCard.available')}
                  </span>
                  {currentStock <= 10 && currentStock > 0 && (
                    <span className="text-orange-600 font-medium">
                      {t('product_detail.limited_stock')}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Quantity Selector */}
            <div className="space-y-3">
              <div className="text-lg font-semibold">
                {t('productCard.quantity')}
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center border rounded-xl overflow-hidden shadow-sm">
                  <button
                    onClick={() => handleQuantityChange(quantity - 1)}
                    disabled={quantity <= 1}
                    className="px-4 py-3 hover:bg-gray-50 disabled:opacity-40 transition-colors"
                  >
                    <span className="text-xl font-bold text-gray-600">-</span>
                  </button>
                  <span className="px-6 py-3 text-xl font-bold min-w-[60px] text-center">
                    {quantity}
                  </span>
                  <button
                    onClick={() => handleQuantityChange(quantity + 1)}
                    disabled={quantity >= currentStock}
                    className="px-4 py-3 hover:bg-gray-50 disabled:opacity-40 transition-colors"
                  >
                    <span className="text-xl font-bold text-gray-600">+</span>
                  </button>
                </div>
                <div className="text-sm text-gray-500">
                  <p>{t('product_detail.max_quantity', { max: currentStock })}</p>
                  {currentStock <= 5 && currentStock > 0 && (
                    <p className="text-orange-600 mt-1">
                      {t('product_detail.hurry_up')}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4">
              <Button
                variant="primary"
                size="lg"
                icon={<AddShoppingCart />}
                onClick={handleAddToCart}
                disabled={currentStock <= 0}
                className="h-14 w-full"
              >
                {currentStock > 0
                  ? t('productCard.addToCart')
                  : t('productCard.outOfStock')}
              </Button>
              <Button
                variant="outline"
                size="lg"
                icon={<ArrowBack />}
                onClick={() => navigate('/products')}
                className="h-14 w-full"
              >
                {t('product_detail.back_to_products')}
              </Button>
            </div>

            {/* Delivery & Warranty Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              <div className="border border-blue-100 rounded-xl p-4 bg-white">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <LocalShipping className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-blue-700">
                      {t('product_detail.fast_delivery')}
                    </div>
                    <div className="text-xs text-gray-600">
                      {branch?.name ? 
                        `${t('product_detail.delivery_to')} ${branch.name}: 2-3 ${t('product_detail.days')}` :
                        `2-3 ${t('product_detail.days')}`
                      }
                    </div>
                  </div>
                </div>
              </div>
              <div className="border border-green-100 rounded-xl p-4 bg-white">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <Shield className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-green-700">
                      {t('product_detail.warranty')}
                    </div>
                    <div className="text-xs text-gray-600">
                      {t('product_detail.warranty_period', { period: '1 سنة' })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Store Info */}
            {current_store && (
              <div className="border rounded-xl p-4 mt-4 bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <StoreMallDirectoryRounded className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">
                        {t('product_detail.sold_by')}
                      </div>
                      <div className="text-sm text-gray-600">
                        {current_store.name}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedStore(current_store);
                      navigate(`/store/${current_store.name}`);
                    }}
                  >
                    {t('product_detail.visit_store')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product Tabs */}
      <div className="mt-12">
        {/* Tabs Header */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setTabValue(0)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                tabValue === 0
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('product_detail.description')}
            </button>
            <button
              onClick={() => setTabValue(1)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                tabValue === 1
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('product_detail.specifications')}
            </button>
            <button
              onClick={() => setTabValue(2)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                tabValue === 2
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('product_detail.reviews', { count: reviews.length })}
            </button>
          </nav>
        </div>

        {/* Description Tab */}
        <TabPanel value={tabValue} index={0}>
          {description && (
            <div
              className="prose max-w-none bg-white rounded-2xl p-6 shadow-sm"
              dangerouslySetInnerHTML={{
                __html:
                  i18n.language.startsWith('ar')
                    ? selectedProduct.descriptionAR || ''
                    : selectedProduct.description || '',
              }}
            />
          )}
        </TabPanel>

        {/* Specifications Tab */}
        <TabPanel value={tabValue} index={1}>
          <div className="border rounded-xl p-6 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Color Information */}
              {selectedColor && (
                <div>
                  <div className="text-sm font-semibold text-gray-500 mb-2">
                    {t('product_detail.selected_color')}
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-full border"
                      style={{ backgroundColor: selectedColor.color_name.toLowerCase() }}
                    />
                    <div>{selectedColor.color_name}</div>
                  </div>
                </div>
              )}

              {/* Stock Information */}
              <div>
                <div className="text-sm font-semibold text-gray-500 mb-2">
                  {t('product_detail.stock_info')}
                </div>
                <div>
                  {currentStock} {t('productCard.available')}
                </div>
              </div>

              {/* Branch Information */}
              <div>
                <div className="text-sm font-semibold text-gray-500 mb-2">
                  {t('product_detail.branch_location')}
                </div>
                <div>{branch?.name || 'N/A'}</div>
              </div>

              {/* Store Information */}
              {current_store && (
                <div>
                  <div className="text-sm font-semibold text-gray-500 mb-2">
                    {t('product_detail.store_info')}
                  </div>
                  <div>{current_store.name}</div>
                </div>
              )}
            </div>
          </div>
        </TabPanel>

        {/* Reviews Tab */}
        <TabPanel value={tabValue} index={2}>
          <div className="space-y-6">
            {/* Write Review */}
            <div className="border rounded-xl p-6 bg-white">
              <div className="text-lg font-semibold mb-4">
                {t('product_detail.write_review')}
              </div>
              {reviewSuccess && <Alert type="success" message={reviewSuccess} />}
              {reviewError && <Alert type="error" message={reviewError} />}
              <div className="space-y-4">
                <Rating
                  value={rating}
                  onChange={(event, newValue) => setRating(newValue)}
                  precision={0.5}
                  size="large"
                  icon={<Star fontSize="inherit" className="text-yellow-400" />}
                  emptyIcon={
                    <StarBorderOutlined fontSize="inherit" className="text-gray-300" />
                  }
                />
                <TextField
                  label={t('product_detail.your_review')}
                  multiline
                  rows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  variant="outlined"
                  fullWidth
                  className="bg-gray-50 rounded-lg"
                />
                <Button
                  variant="primary"
                  size="md"
                  icon={<Send />}
                  onClick={handleSubmitReview}
                  disabled={productLoading}
                  className="self-end"
                >
                  {t('product_detail.submit_review')}
                </Button>
              </div>
            </div>

            {/* Reviews List */}
            {reviews.length > 0 ? (
              <div className="space-y-4">
                <div className="text-lg font-semibold text-gray-700">
                  {reviews.length} {t('product_detail.reviews_count')}
                </div>
                {reviews.map((review) => (
                  <div key={review.id} className="border rounded-xl p-5 bg-white hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary to-primary-dark flex items-center justify-center text-white font-bold">
                        {review.user[0]}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-semibold">
                            {review.user}
                          </div>
                          <div className="text-xs text-gray-400">
                            {new Date(review.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <Rating
                          value={review.rating}
                          readOnly
                          precision={0.5}
                          size="small"
                          icon={<Star fontSize="inherit" className="text-yellow-400" />}
                          emptyIcon={
                            <StarBorderOutlined fontSize="inherit" className="text-gray-300" />
                          }
                          className="mb-2"
                        />
                        <div className="text-sm text-gray-600">
                          {review.comment}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border rounded-xl p-8 text-center">
                <StarBorderOutlined className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <div className="text-gray-500">
                  {t('product_detail.no_reviews')}
                </div>
              </div>
            )}
          </div>
        </TabPanel>
      </div>
    </div>
  );
}